import React, { useState, useEffect } from 'react';
import { get, post } from 'aws-amplify/api';

const Seats = ({ user }) => {
  const [seats, setSeats] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [adminMode, setAdminMode] = useState(false);
  
  const isAdmin = user?.signInUserSession?.accessToken?.payload?.['cognito:groups']?.includes('Admins');

  useEffect(() => {
    fetchSeats();
  }, []);

  const fetchSeats = async () => {
    try {
      const response = await get({
        apiName: 'navy-football-api',
        path: '/seats'
      }).response;
      
      const seatsData = await response.body.json();
      setSeats(seatsData);
    } catch (error) {
      console.error('Error fetching seats:', error);
    }
  };

  const reserveSeat = async (row, seat) => {
    setLoading(true);
    setMessage('');

    try {
      await post({
        apiName: 'navy-football-api',
        path: '/seats',
        options: {
          body: {
            row: row,
            seat: seat,
            username: user.username
          }
        }
      }).response;

      setMessage('Seat reserved successfully!');
      fetchSeats(); // Refresh seats
    } catch (error) {
      setMessage('Error reserving seat. Please try again.');
      console.error('Error reserving seat:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeatStatus = (row, seat) => {
    if (!seats[row] || !seats[row][seat]) return 'available';
    const seatData = seats[row][seat];
    
    if (seatData.admin_unavailable) {
      return 'admin-unavailable';
    }
    
    if (!seatData.available) {
      if (seatData.reserved_by === user.username) {
        return 'my-reservation';
      }
      return 'reserved';
    }
    return 'available';
  };

  const getSeatClass = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-500 hover:bg-green-600 cursor-pointer';
      case 'reserved':
        return 'bg-red-500 cursor-not-allowed';
      case 'my-reservation':
        return 'bg-blue-500 cursor-default';
      case 'admin-unavailable':
        return 'bg-gray-500 cursor-not-allowed';
      default:
        return 'bg-gray-300';
    }
  };

  const toggleSeatAvailability = async (row, seat) => {
    setLoading(true);
    setMessage('');

    try {
      await post({
        apiName: 'navy-football-api',
        path: '/admin/seats',
        options: {
          body: {
            action: 'toggle_availability',
            row: row,
            seat: seat
          }
        }
      }).response;

      setMessage('Seat availability updated!');
      fetchSeats();
    } catch (error) {
      setMessage('Error updating seat availability.');
      console.error('Error updating seat:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-navy-primary dark:text-gold-primary mb-6">
          Seat Reservations
        </h2>

        {message && (
          <div className={`p-4 rounded-lg mb-6 ${
            message.includes('Error') 
              ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' 
              : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
          }`}>
            {message}
          </div>
        )}

        {isAdmin && (
          <div className="mb-4">
            <button
              onClick={() => setAdminMode(!adminMode)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                adminMode 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'bg-gold-primary text-navy-primary hover:bg-gold-secondary'
              }`}
            >
              {adminMode ? 'Exit Admin Mode' : 'Admin Mode'}
            </button>
            {adminMode && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                Click seats to toggle availability
              </p>
            )}
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-500 rounded"></div>
              <span>Available</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <span>Reserved</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <span>Your Reservation</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-gray-500 rounded"></div>
              <span>Unavailable</span>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            <div className="text-center mb-4 text-lg font-semibold text-navy-primary dark:text-gold-primary">
              FIELD
            </div>
            
            <div className="space-y-2">
              {Array.from({ length: 10 }, (_, rowIndex) => {
                const row = rowIndex + 1;
                return (
                  <div key={row} className="flex items-center space-x-2">
                    <div className="w-8 text-center font-medium text-sm">
                      {row.toString().padStart(2, '0')}
                    </div>
                    <div className="flex space-x-1">
                      {Array.from({ length: 28 }, (_, seatIndex) => {
                        const seat = 28 - seatIndex; // Reverse numbering: 28, 27, 26... 3, 2, 1
                        const status = getSeatStatus(row, seat);
                        
                        return (
                          <button
                            key={seat}
                            onClick={() => {
                              if (adminMode && isAdmin) {
                                toggleSeatAvailability(row, seat);
                              } else if (status === 'available' && !loading) {
                                reserveSeat(row, seat);
                              }
                            }}
                            disabled={(!adminMode && status !== 'available') || loading}
                            className={`w-8 h-8 text-xs font-medium text-white rounded transition-colors ${getSeatClass(status)} ${
                              adminMode && isAdmin ? 'hover:opacity-75' : ''
                            }`}
                            title={`Row ${row.toString().padStart(2, '0')}, Seat ${seat.toString().padStart(2, '0')}`}
                          >
                            {seat.toString().padStart(2, '0')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 text-sm text-gray-600 dark:text-gray-400">
          {adminMode ? (
            <>
              <p>• Click any seat to toggle its availability</p>
              <p>• Gray seats are marked as unavailable by admin</p>
            </>
          ) : (
            <>
              <p>• Click on an available (green) seat to reserve it</p>
              <p>• Seats are automatically released every Sunday</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Seats;